import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "relative" }
const _hoisted_2 = { class: "absolute top-5 right-5" }
const _hoisted_3 = { class: "absolute z-10 top-0 right-0 h-5 w-5 [&>svg]:stroke-current [&>svg]:h-5 [&>svg]:w-5" }
const _hoisted_4 = { class: "flex items-start justify-between w-full p-6" }
const _hoisted_5 = { class: "flex items-start flex-row" }
const _hoisted_6 = { class: "mb-1 text-gray-900 font-semibold text-lg mr-3" }
const _hoisted_7 = { class: "border bg-purple-50 rounded-full py-0.5 px-2 text-blue-600 font-medium text-xs mt-1" }
const _hoisted_8 = { class: "flex justify-start items-center gap-2 rounded-full border border-gray-200 p-1 inline-flex mr-6" }
const _hoisted_9 = /*#__PURE__*/_createElementVNode("hr", null, null, -1)
const _hoisted_10 = { class: "w-full text-left" }
const _hoisted_11 = { class: "text-xs text-gray-600 font-medium pl-6 py-3 border-b border-gray-200" }
const _hoisted_12 = { class: "text-xs text-gray-600 font-medium py-3 border-b border-gray-200 relative" }
const _hoisted_13 = { class: "absolute" }
const _hoisted_14 = {
  key: 0,
  class: "absolute z-10 -top-0.5 -left-6 h-5 w-5 [&>svg]:stroke-current [&>svg]:h-5 [&>svg]:w-5"
}
const _hoisted_15 = {
  key: 1,
  class: "absolute z-10 -top-0.5 -left-6 h-5 w-5 [&>svg]:stroke-current [&>svg]:h-5 [&>svg]:w-5"
}
const _hoisted_16 = { class: "text-xs text-gray-600 font-medium pr-6 py-3 border-b border-gray-200" }
const _hoisted_17 = { class: "text-gray-900 font-sm font-medium pl-6 py-6 border-b border-gray-200" }
const _hoisted_18 = { class: "text-sm text-gray-600 py-6 border-b border-gray-200" }
const _hoisted_19 = { class: "flex flex-row gap-3 items-center pr-6 py-6 border-b border-gray-200" }
const _hoisted_20 = { class: "bg-gray-200 rounded-full h-[8px] min-w-36 w-full" }
const _hoisted_21 = { class: "text-gray-700 font-sm font-medium" }
const _hoisted_22 = { class: "flex justify-between items-center mt-3 mb-4 px-6" }
const _hoisted_23 = { class: "text-gray-700 text-sm font-medium" }
const _hoisted_24 = ["disabled"]
const _hoisted_25 = ["disabled"]

import { useActivityStore } from "@/stores/activityStore";
import { ref, computed } from "vue";
import { useSchoolStore } from "@/stores/schoolStore";

import FiltersOutOfDateComponent from "@/modules/main/components/FiltersOutOfDateComponent.vue";
import LoadingSpinnerComponent from "@/modules/main/components/LoadingSpinnerComponent.vue";
import { SchoolStructure } from "@/interfaces/school-structure.interface";
import { ActivitySummaryPerClassData } from "@/interfaces/activities.interface";

const itemsPerPage = 7;


export default /*#__PURE__*/_defineComponent({
  __name: 'ActivitySummaryPerClassComponent',
  setup(__props) {

const activityStore = useActivityStore();
const schoolStore = useSchoolStore();

const classList = computed(
  () =>
    schoolStore?.currentSchoolStructure?.classGroups.map((classGroup) => ({
      name: classGroup.name,
      _id: classGroup._id,
    })) as unknown as SchoolStructure[]
);

const summaryData = computed(() => {
  return activityStore?.getActivitySummaryPerClassData as unknown as ActivitySummaryPerClassData[];
});

// Map the summaryData object to display the data
const mappedData = computed(() => {
  if (summaryData.value) {
    return summaryData.value.map((item) => {
      // Find the matching class group based on _id
      const matchingClassGroup = classList.value.find(
        (classGroup) => classGroup._id === item.classGroupId
      );
      return {
        classGroupId: item.classGroupId,
        className: matchingClassGroup ? matchingClassGroup.name : "Unknown", // Return the name or 'Unknown' if no match
        averageLearningTimeMonth: Math.floor(item.averageLearningTimeMonth),
        averageLearningTimeWeek: Math.floor(item.averageLearningTimeWeek),
        percentageDailyTasksCompletionMonth:
          item.percentageDailyTasksCompletionMonth,
        percentageDailyTasksCompletionWeek:
          item.percentageDailyTasksCompletionWeek,
      };
    });
  } else {
    return [];
  }
});

const selectedActivity = ref("Last 30 days");
const currentPage = ref(1);
const activeActivity = ref(
  "border border-gray-200 rounded-full text-sm py-2.5 px-5 font-semibold text-gray-700"
);

const inactiveActivity = ref(
  "py-2.5 px-5 border-transparent rounded-full text-sm font-semibold text-gray-500"
);

const classCount = computed(
  () => activityStore?.activitySummaryPerClassData?.length
);

// Calculate total pages based on the items per page
const totalPages = computed(() => {
  return Math.ceil(mappedData.value.length / itemsPerPage);
});

// Get the data for the current page
const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return mappedData.value.slice(start, end);
});

function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
}
// Go to the next page
function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
}

// Generate progress bar background for activity cards
const generateColoursFromPercentage = (percentage: number) => {
  if (percentage === 0) {
    return "bg-gray-200";
  }

  // Red Color
  if (percentage <= 33) {
    return "bg-red-300";
  }

  // Yellow Color
  if (percentage > 33 && percentage <= 66) {
    return "bg-yellow-400";
  }

  // Green Color
  if (percentage > 66) {
    return "bg-emerald-200";
  }
};

return (_ctx: any,_cache: any) => {
  const _component_rl_icon = _resolveComponent("rl-icon")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(LoadingSpinnerComponent),
    _createVNode(FiltersOutOfDateComponent),
    _createElementVNode("div", _hoisted_2, [
      _withDirectives((_openBlock(), _createElementBlock("i", _hoisted_3, [
        _createVNode(_component_rl_icon, {
          icon: 'rl-info-circle',
          title: 'rl info circle',
          "aria-hidden": "true"
        })
      ])), [
        [
          _directive_tooltip,
          _ctx.$t('activity_view.tooltips.activity_summary_class'),
          "left",
          { tooltip: true }
        ]
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", null, [
          _createElementVNode("h2", _hoisted_6, _toDisplayString(_ctx.$t("activity_view.activity_summary_per_class_card.header")), 1)
        ]),
        _createElementVNode("div", _hoisted_7, _toDisplayString(classCount.value) + " " + _toDisplayString(_ctx.$t("activity_view.activity_summary_per_class_card.classes")), 1)
      ]),
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (selectedActivity.value = 'Last 30 days')),
          class: _normalizeClass(
            (selectedActivity.value === 'Last 30 days'
              ? activeActivity.value
              : inactiveActivity.value) + ' px-2'
          )
        }, _toDisplayString(_ctx.$t("activity_view.activity_summary_per_class_card.last_30_days")), 3),
        _createElementVNode("button", {
          onClick: _cache[1] || (_cache[1] = ($event: any) => (selectedActivity.value = 'Last 7 days')),
          class: _normalizeClass(
            (selectedActivity.value === 'Last 7 days'
              ? activeActivity.value
              : inactiveActivity.value) + ' px-2'
          )
        }, _toDisplayString(_ctx.$t("activity_view.activity_summary_per_class_card.last_7_days")), 3)
      ])
    ]),
    _hoisted_9,
    _createElementVNode("table", _hoisted_10, [
      _createElementVNode("thead", null, [
        _createElementVNode("tr", null, [
          _createElementVNode("th", _hoisted_11, _toDisplayString(_ctx.$t("activity_view.activity_summary_per_class_card.class")), 1),
          _createElementVNode("th", _hoisted_12, [
            _createElementVNode("div", _hoisted_13, [
              (selectedActivity.value === 'Last 30 days')
                ? _withDirectives((_openBlock(), _createElementBlock("i", _hoisted_14, [
                    _createVNode(_component_rl_icon, {
                      icon: 'rl-info-circle',
                      title: 'rl info circle',
                      "aria-hidden": "true"
                    })
                  ])), [
                    [
                      _directive_tooltip,
                      _ctx.$t('activity_view.tooltips.class_activity_30_days'),
                      "left",
                      { tooltip: true }
                    ]
                  ])
                : _createCommentVNode("", true),
              (selectedActivity.value === 'Last 7 days')
                ? _withDirectives((_openBlock(), _createElementBlock("i", _hoisted_15, [
                    _createVNode(_component_rl_icon, {
                      icon: 'rl-info-circle',
                      title: 'rl info circle',
                      "aria-hidden": "true"
                    })
                  ])), [
                    [
                      _directive_tooltip,
                      _ctx.$t('activity_view.tooltips.class_activity_7_days'),
                      "left",
                      { tooltip: true }
                    ]
                  ])
                : _createCommentVNode("", true)
            ]),
            _createTextVNode(" " + _toDisplayString(_ctx.$t("activity_view.activity_summary_per_class_card.average_minutes")), 1)
          ]),
          _createElementVNode("th", _hoisted_16, _toDisplayString(_ctx.$t("activity_view.activity_summary_per_class_card.daily_tasks")), 1)
        ])
      ]),
      _createElementVNode("tbody", null, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedData.value, (data, index) => {
          return (_openBlock(), _createElementBlock("tr", { key: index }, [
            _createElementVNode("td", _hoisted_17, _toDisplayString(data.className), 1),
            _createElementVNode("td", _hoisted_18, _toDisplayString(selectedActivity.value === "Last 30 days"
                ? Math.floor(data.averageLearningTimeMonth / 60)
                : Math.floor(data.averageLearningTimeWeek / 60)) + " " + _toDisplayString(_ctx.$t("activity_view.activity_summary_per_class_card.min")), 1),
            _createElementVNode("td", _hoisted_19, [
              _createElementVNode("div", _hoisted_20, [
                _createElementVNode("div", {
                  class: _normalizeClass(
                  generateColoursFromPercentage(
                    Math.min(
                      Number(
                        selectedActivity.value === 'Last 30 days'
                          ? data.percentageDailyTasksCompletionMonth
                          : data.percentageDailyTasksCompletionWeek
                      ),
                      100
                    )
                  ) + ' h-[8px] rounded-full'
                ),
                  style: _normalizeStyle(
                  'width: ' +
                  (selectedActivity.value === 'Last 30 days'
                    ? data.percentageDailyTasksCompletionMonth
                    : data.percentageDailyTasksCompletionWeek) +
                  '%'
                )
                }, null, 6)
              ]),
              _createElementVNode("span", _hoisted_21, _toDisplayString(selectedActivity.value === "Last 30 days"
                  ? data.percentageDailyTasksCompletionMonth.toFixed(0)
                  : data.percentageDailyTasksCompletionWeek.toFixed(0)) + "% ", 1)
            ])
          ]))
        }), 128))
      ])
    ]),
    _createElementVNode("div", _hoisted_22, [
      _createElementVNode("span", _hoisted_23, _toDisplayString(_ctx.$t("activity_view.activity_summary_per_class_card.page")) + " " + _toDisplayString(currentPage.value) + " " + _toDisplayString(_ctx.$t("activity_view.activity_summary_per_class_card.of")) + " " + _toDisplayString(totalPages.value), 1),
      _createElementVNode("div", null, [
        _createElementVNode("button", {
          onClick: prevPage,
          disabled: currentPage.value === 1,
          class: "text-gray-700 text-sm font-semibold border border-gray-300 px-3 py-2 bg-white rounded-lg disabled:opacity-50 mr-3"
        }, _toDisplayString(_ctx.$t("activity_view.activity_summary_per_class_card.previous")), 9, _hoisted_24),
        _createElementVNode("button", {
          onClick: nextPage,
          disabled: currentPage.value === totalPages.value,
          class: "text-gray-700 text-sm font-semibold border border-gray-300 px-3 py-2 bg-white rounded-lg disabled:opacity-50"
        }, _toDisplayString(_ctx.$t("activity_view.activity_summary_per_class_card.next")), 9, _hoisted_25)
      ])
    ])
  ]))
}
}

})